import { formatDate, projectTimezone } from '@/lib/formatters';
import { addDays, dayInZone, wallClockTime as zonedWallClockTime, type CalendarDay } from '@shared/wall-clock';

/**
 * Snoozing a task: it leaves the Board and comes back at SNOOZE_HOUR on the day chosen, in the
 * project's timezone (so everyone sees the same morning).
 */

export const SNOOZE_HOUR = 8;

/** The quick choices in the Snooze menu, in days from today. */
export const SNOOZE_PRESETS: Array<{ days: number; label: string }> = [
  { days: 1, label: 'Tomorrow' },
  { days: 2, label: 'In 2 days' },
  { days: 3, label: 'In 3 days' },
  { days: 7, label: 'In a week' },
  { days: 14, label: 'In 2 weeks' },
];

export type { CalendarDay };
export { addDays };

/** The project's timezone (Project settings), or UTC. */
export const projectZone = projectTimezone;

/** Today's date where the project is. */
export function projectToday(now = new Date(), timeZone = projectZone()): CalendarDay {
  return dayInZone(now, timeZone);
}

/** The moment it's `hour`:00 on `date` in `timeZone` (checked twice, for a clock change that day). */
export function wallClockTime(date: CalendarDay, hour: number, timeZone = projectZone()): Date {
  return zonedWallClockTime(date, hour, timeZone);
}

/** When a snooze of `days` from today ends. */
export function snoozeUntil(days: number, now = new Date(), timeZone = projectZone()): Date {
  return wallClockTime(addDays(projectToday(now, timeZone), days), SNOOZE_HOUR, timeZone);
}

/** "Sat 27 Sept" (with the year when it isn't this one). */
export function snoozeDay(at: Date | string): string {
  const date = new Date(at);
  const thisYear = formatDate(date, { year: 'numeric' }) === formatDate(new Date(), { year: 'numeric' });
  return formatDate(date, thisYear ? { weekday: 'short', day: 'numeric', month: 'short' } : { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

/** Snoozed and not back yet. */
export function isSnoozed(snoozedUntil: string | null | undefined, now = Date.now()): boolean {
  return Boolean(snoozedUntil) && new Date(snoozedUntil!).getTime() > now;
}
